import { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styles from './index.module.scss';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { MinusCircleOutlined } from '@ant-design/icons';
import { getMaturity } from '../../../../../../../services/api/thermalControl/mixtures';
import MaturityMethods from '../../../../../../../constants/maturityMethods';
import AntdFormItem from '../../../../../../../components/AntdFormItem';
import AntdDatePicker from '../../../../../../../components/AntdDatePicker';
import dayjs from 'dayjs';
import AntdInput from '../../../../../../../components/AntdInput';

const { wrapper, addNewRowButton, mainWrapper, disabled } = styles;

const parseParams = (params) => {
  return params?.map((param) => ({
    ...param,
    time: moment(param.datetime).format('HH:mm'),
  }));
};

const MixtureParams = ({
  state: { value: _params },
  mixtureId,
  startDate,
  method,
  onChange,
  onWaiting,
}) => {
  const timer = useRef();
  const intl = useIntl();
  const [params, setParams] = useState(parseParams(_params) || []);
  const [isCalculatingMaturity, setIsCalculatingMaturity] = useState(false);

  const getColumnMethod = () => {
    return `strength.thermalcontrol.mixtures.create.params.${
      method === MaturityMethods.NURSE_SAUL ? 'maturity' : 'equivalentAge'
    }`;
  };

  const addNewRow = () => {
    setParams((prev) => [
      ...prev,
      { datetime: undefined, maturity: undefined, resistance: undefined },
    ]);
  };

  const removeRow = (index) => {
    const newList = [...params];
    newList.splice(index, 1);

    setParams(newList);
  };

  const isReadyToAdd = () => {
    return params.every((param) => !Object.values(param).includes(undefined));
  };

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    onWaiting();
    timer.current = setTimeout(() => {
      const newParams = [...params];

      onChange(
        newParams.map((param) => ({
          maturity: param.maturity,
          datetime: param.datetime,
          resistance: parseFloat(param.resistance),
        })),
      );
    }, 1000);
  }, [onChange, onWaiting, params]);

  useEffect(() => {
    if (!_params) {
      setParams([]);
    }
  }, [_params]);

  return (
    <div className={mainWrapper}>
      <div>
        {params.map((param, index) => {
          return (
            <div className={wrapper} key={`Ch${index + 1}`}>
              <AntdFormItem label={intl.formatMessage({ id: 'dates' })}>
                <AntdDatePicker
                  size="small"
                  onChange={async (date) => {
                    const newParams = [...params];
                    setIsCalculatingMaturity(true);
                    const { data } = await getMaturity({
                      id: mixtureId,
                      startDate: startDate,
                      method,
                      endDate: date.toISOString(),
                    });
                    setIsCalculatingMaturity(false);
                    newParams[index].maturity = data.maturity || 0;
                    newParams[index].datetime = date.toISOString();
                    setParams(newParams);
                  }}
                  value={param.datetime ? dayjs(param.datetime) : undefined}
                  disabledDate={(currentDate) => {
                    return (
                      dayjs(currentDate).isBefore(dayjs(startDate), 'day') ||
                      currentDate.isAfter(dayjs(), 'day')
                    );
                  }}
                  disabledTime={(current) => {
                    const disabledHours = [];
                    const disabledMinutes = [];
                    const disabledSeconds = [];
                    const disabledDate = dayjs(startDate);

                    if (dayjs(current).isSame(disabledDate, 'day')) {
                      for (let i = 0; i < 24; i++) {
                        if (i < disabledDate.hour()) {
                          disabledHours.push(i);
                        }
                      }

                      if (current.hour() === disabledDate.hour()) {
                        for (let i = 0; i < 60; i++) {
                          if (i < disabledDate.minute()) {
                            disabledMinutes.push(i);
                          }
                        }

                        if (current.minute() === disabledDate.minute()) {
                          for (let i = 0; i < 60; i++) {
                            if (i < disabledDate.second()) {
                              disabledSeconds.push(i);
                            }
                          }
                        }
                      }
                    }

                    return {
                      disabledHours: () => disabledHours,
                      disabledMinutes: () => disabledMinutes,
                      disabledSeconds: () => disabledSeconds,
                    };
                  }}
                  status={!param.datetime ? 'error' : undefined}
                />
              </AntdFormItem>
              <AntdFormItem
                label={intl.formatMessage({ id: getColumnMethod() })}
                validateStatus={
                  isCalculatingMaturity ? 'validating' : undefined
                }
                hasFeedback
              >
                <AntdInput value={param.maturity} size="small" />
              </AntdFormItem>
              <AntdFormItem
                label={
                  <>
                    {intl.formatMessage({ id: 'resistance' })}
                    <span
                      style={{ textTransform: 'none', whiteSpace: 'pre' }}
                    >{` (MPa)`}</span>
                  </>
                }
              >
                <AntdInput
                  type="number"
                  size="small"
                  value={param.resistance}
                  status={!param.resistance ? 'error' : undefined}
                  onChange={({ target: { value } }) => {
                    const newParams = [...params];
                    newParams[index].resistance = value;
                    setParams(newParams);
                  }}
                />
              </AntdFormItem>
              <MinusCircleOutlined
                onClick={() => {
                  removeRow(index);
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        className={`${addNewRowButton} ${!isReadyToAdd() ? disabled : ''}`}
        onClick={() => {
          addNewRow();
        }}
      >
        + {intl.formatMessage({ id: 'button.add' }).toUpperCase()}
      </div>
    </div>
  );
};

MixtureParams.propTypes = {
  state: PropTypes.shape({ value: PropTypes.arrayOf(PropTypes.object) })
    .isRequired,
  mixtureId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  method: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onWaiting: PropTypes.func.isRequired,
};

export default MixtureParams;
